"use client";

/** Fixed by the backend: `OtpService` mints six digits and both OTP DTOs reject anything else. */
export const CODE_LENGTH = 6;

/**
 * Digits only, capped at the code length. Emails and password managers hand the code over in
 * whatever shape they please — `4 5 2 3 8 6`, `452-386`, a trailing newline — and all of them
 * reduce to the six digits the backend wants.
 */
export const toCode = (value: string) => value.replace(/\D/g, "").slice(0, CODE_LENGTH);

type CodeInputProps = {
  value: string;
  onChange: (code: string) => void;
  /** Names the field for its label. Distinct ids matter once a page carries more than one. */
  id?: string;
  label?: string;
  autoFocus?: boolean;
};

/**
 * The one-time code field: oversized digits over a rule, shared by email verification and password
 * reset. Both consume the same `OtpService` codes, so the entry rules belong in one place.
 */
export function CodeInput({ value, onChange, id = "code", label = "Verification code", autoFocus }: CodeInputProps) {
  return (
    <div className="flex flex-col items-center gap-16">
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <input
        id={id}
        name={id}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="one-time-code"
        autoFocus={autoFocus}
        required
        value={value}
        onKeyDown={(event) => {
          if (event.key.length === 1 && !/\d/.test(event.key) && !event.ctrlKey && !event.metaKey) {
            event.preventDefault();
          }
        }}
        /**
         * A pasted code replaces the field outright, however it was copied. Deliberately no
         * `maxLength`: the browser would clip the raw text to six *characters* before `toCode`
         * could drop the separators.
         */
        onPaste={(event) => {
          const pasted = toCode(event.clipboardData.getData("text"));

          if (pasted) {
            event.preventDefault();
            onChange(pasted);
          }
        }}
        onChange={(event) => onChange(toCode(event.target.value))}
        className="w-260 bg-transparent text-center indent-12 font-sans text-[44px] font-semibold leading-[1.3] tracking-[12px] text-neutral-black outline-none"
      />
      {/* Figma `Line 56` — a 4px #13AECE rule with round caps, not an image. */}
      <span className="h-4 w-260 shrink-0 rounded-circle bg-brand-blue" />
    </div>
  );
}
